import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Sun, Moon, Cloud, RefreshCw, Loader2, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, Brain, Target, AlertTriangle, Zap,
  DollarSign, BarChart3, Activity
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INSTRUMENT_ICONS = {
  "Gold (XAUUSD)": DollarSign,
  "S&P 500": BarChart3,
  "NASDAQ 100": BarChart3,
  "EUR/USD": Activity,
  "GBP/USD": Activity,
  "USD/JPY": Activity,
  "DXY (Dollar Index)": Activity,
  "VIX": AlertTriangle,
};

const INSTRUMENT_ROUTES = {
  "Gold (XAUUSD)": "/aura-xauusd",
  "S&P 500": "/aura-sp500",
  "NASDAQ 100": "/aura-nasdaq",
  "EUR/USD": "/aura-eurusd",
  "GBP/USD": "/aura-gbpusd",
  "USD/JPY": "/aura-usdjpy",
  "DXY (Dollar Index)": "/aura-dxy",
};

export const MorningBriefing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [scan, setScan] = useState(null);
  const [briefing, setBriefing] = useState(null);

  useEffect(() => {
    fetchScan();
  }, []);

  const fetchScan = async () => {
    try {
      const response = await axios.get(`${API}/morning-briefing/scan`);
      setScan(response.data);
    } catch (error) {
      console.error("Error fetching scan:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateBriefing = async () => {
    setGenerating(true);
    try {
      const response = await axios.get(`${API}/morning-briefing/generate`, { timeout: 120000 });
      setBriefing(response.data);
      toast.success("Morning briefing generated!");
    } catch (error) {
      console.error("Error generating briefing:", error);
      toast.error("Failed to generate briefing - try again");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const summary = scan?.summary;
  const instruments = scan?.instruments || [];

  // Determine market mood
  let MoodIcon = Cloud;
  let moodColor = "text-yellow-500";
  let moodLabel = "Mixed";
  
  if (summary?.bullish_count > summary?.bearish_count + 2) {
    MoodIcon = Sun;
    moodColor = "text-green-500";
    moodLabel = "Risk On";
  } else if (summary?.bearish_count > summary?.bullish_count + 2) {
    MoodIcon = Moon;
    moodColor = "text-red-500";
    moodLabel = "Risk Off";
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sun className="w-6 h-6 text-amber-500" />
            Morning Briefing
          </h1>
          <p className="text-muted-foreground">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <Button onClick={generateBriefing} disabled={generating}>
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Brain className="w-4 h-4 mr-2" />
              Generate AI Brief
            </>
          )}
        </Button>
      </div>

      {/* Market Mood */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-4 rounded-full bg-muted ${moodColor}`}>
                <MoodIcon className="w-8 h-8" />
              </div>
              <div>
                <div className="text-2xl font-bold">{moodLabel}</div>
                <div className="text-sm text-muted-foreground">
                  {summary?.bullish_count} bullish, {summary?.bearish_count} bearish, {summary?.neutral_count} neutral
                </div>
              </div>
            </div>
            {summary?.biggest_mover && (
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Biggest Mover</div>
                <div className="font-bold">{summary.biggest_mover.name}</div>
                <div className={summary.biggest_mover.change_pct >= 0 ? 'text-green-500' : 'text-red-500'}>
                  {summary.biggest_mover.change_pct >= 0 ? '+' : ''}{summary.biggest_mover.change_pct}%
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Instruments Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {instruments.map((inst) => {
          const Icon = INSTRUMENT_ICONS[inst.name] || Activity;
          const route = INSTRUMENT_ROUTES[inst.name];
          
          return (
            <Card 
              key={inst.symbol} 
              className={`cursor-pointer hover:border-primary/50 transition-all ${
                inst.trend === 'BULLISH' ? 'border-green-500/30' : 
                inst.trend === 'BEARISH' ? 'border-red-500/30' : ''
              }`}
              onClick={() => route && navigate(route)}
            >
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Icon className={`w-4 h-4 ${
                    inst.trend === 'BULLISH' ? 'text-green-500' : 
                    inst.trend === 'BEARISH' ? 'text-red-500' : 'text-muted-foreground'
                  }`} />
                  <Badge variant={
                    inst.trend === 'BULLISH' ? 'success' : 
                    inst.trend === 'BEARISH' ? 'destructive' : 'secondary'
                  } className="text-xs">
                    {inst.trend}
                  </Badge>
                </div>
                <div className="font-medium text-sm">{inst.name}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-lg font-bold">${inst.price}</span>
                  <span className={`flex items-center text-sm ${
                    inst.change_pct >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {inst.change_pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {inst.change_pct}%
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  RSI: {inst.rsi}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* AI Briefing */}
      {briefing && (
        <Card className="border-primary/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-primary" />
                AI Morning Briefing
              </CardTitle>
              <Badge variant="outline">{briefing.model}</Badge>
            </div>
            <CardDescription>
              {briefing.date} | Generated {new Date(briefing.generated_at).toLocaleTimeString()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
              {briefing.briefing}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Button variant="outline" className="h-auto py-4" onClick={() => navigate('/challenge')}>
          <div className="text-center">
            <Target className="w-6 h-6 mx-auto mb-1 text-amber-500" />
            <div className="text-sm">My Challenge</div>
          </div>
        </Button>
        <Button variant="outline" className="h-auto py-4" onClick={() => navigate('/aura-xauusd')}>
          <div className="text-center">
            <DollarSign className="w-6 h-6 mx-auto mb-1 text-yellow-500" />
            <div className="text-sm">Gold Thesis</div>
          </div>
        </Button>
        <Button variant="outline" className="h-auto py-4" onClick={() => navigate('/aura-sp500')}>
          <div className="text-center">
            <BarChart3 className="w-6 h-6 mx-auto mb-1 text-blue-500" />
            <div className="text-sm">S&P 500</div>
          </div>
        </Button>
        <Button variant="outline" className="h-auto py-4" onClick={() => navigate('/aura-nasdaq')}>
          <div className="text-center">
            <BarChart3 className="w-6 h-6 mx-auto mb-1 text-purple-500" />
            <div className="text-sm">NASDAQ</div>
          </div>
        </Button>
      </div>
    </div>
  );
};

export default MorningBriefing;
